
import { ArrowBack } from "@mui/icons-material";
import { AppBar, Box, Button, CircularProgress, Grid, IconButton, Toolbar, Typography } from "@mui/material";
import makeStyles from '@mui/styles/makeStyles';
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import DocViewer, { DocViewerRenderers } from '@cyntler/react-doc-viewer';
import { usePDFReport } from "./PDFReport";
import { pdf } from '@react-pdf/renderer';
import { getBrowserInfo } from "../../../components/DataGridV2/utils";
import { questionnaireDetailActions } from "../../../_actions/questionnaireDetail.actions";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useParams } from "react-router-dom/cjs/react-router-dom.min";
import { QuestionnaireItemType, QuestionnaireStatus, toBase64 } from "../../../_helpers";
import { questionnaireUpdateStatus } from "../../../apis/questionnaireApis";

const { iOSSafari } = getBrowserInfo()

const DocumentViewer = React.memo(({ classes, document, ...props }) => {
    return (
        <DocViewer
            config={{
                header: {
                    disableHeader: true,
                    disableFileName: true
                },
                pdfVerticalScrollByDefault: true
            }}
            documents={document}
            className={classes.myDoc}
            pluginRenderers={DocViewerRenderers}
            style={{ width: '100%' }} />
    )
}, (p, c) => {
    return p.document === c.document;
});

const PreviewPage = () => {
    const [document, setDocument] = useState([]);
    const [loading, setLoading] = useState(true);
    const classes = useStyles();
    const dispatch = useDispatch()
    const history = useHistory();
    const { questionnaireId } = useParams();
    const { questionnaireStore: { questionnaireDetails: { questionnaireDetails } } } = useSelector(state => state);
    const isProject = questionnaireDetails?.projectId && !questionnaireDetails?.buildingId;
    const projectObjectNumber = isProject ? questionnaireDetails?.projectNo : questionnaireDetails?.buildingNoExtern

    useEffect(() => {
        dispatch(questionnaireDetailActions.fetchQuestionnaireDetails(questionnaireId))
    }, [questionnaireId])

    useEffect(() => {
    if (!questionnaireDetails) return;
        if (questionnaireDetails?.status !== QuestionnaireStatus.InProgress) {
            handleGoBack()
        }
    }, [questionnaireDetails]);

const isAllAnswerGiven = useMemo(() => {
    if (!questionnaireDetails?.sections) return false;
    return questionnaireDetails.sections.every(sec =>
        sec.questions.every(q => {
            const val = q.type === QuestionnaireItemType.Signature
                ? (q.signature?.content && q.answers?.length > 0) 
                : q.answers;

            if (q.required) {
                if (Array.isArray(val)) return val.filter(Boolean).length > 0;
                return !!val;
            }
            return true;
        })
    );
}, [questionnaireDetails]);

    const pdfReport = usePDFReport(questionnaireDetails);

    useEffect(() => {
        if (!questionnaireDetails) return;

        setLoading(true);

        const fetchReport = async () => {
            const success = await loadReport();
            if (success !== true) {
                //Give it another try when fails.
                await loadReport();
            }
            setLoading(false);
        };

        fetchReport();
    }, [questionnaireDetails]);

    const loadReport = async () => {
        if (!document?.length)
            try {
                const blob = await pdf(pdfReport).toBlob();
                const url = window.URL.createObjectURL(blob);
                setDocument([
                    {
                        fileName: `${questionnaireDetails.title}.pdf`,
                        uri: url,
                        fileType: 'application/pdf',
                    },
                ]);
                return true;
            } catch (error) {
                console.error('Failed to load offline report:', error);
                return false;
            }
    };

    const { t } = useTranslation()

    const handleGoBack = () => {
        history.push(`/vragenlijst/${questionnaireId}`);
    };
    const handleUpdateStatusChange = async () => {
        const pdfFile = await toBase64(await pdf(pdfReport).toBlob());
        const payload = {
            questionnaireId: questionnaireId,
            status: QuestionnaireStatus.Completed,
            report: {
                name: "questionnaire.pdf",
                content: pdfFile.split(',')[1],
            }
        };
        try {
            await questionnaireUpdateStatus(questionnaireId, payload);
            dispatch(questionnaireDetailActions.fetchQuestionnaireDetails(questionnaireId));
            history.push({
                pathname: `/vragenlijst/${questionnaireId}`,
                state: { openComplete: true }
            });
        } catch (err) {
            console.error(err);
        }
    };

    const ReportDocumentViewer = useCallback(() => {
        return <DocumentViewer classes={classes} document={document} />;
    }, [document]);

    return (
        <div style={{ height: 'calc(100% - 48px)' }}>
            <AppBar
                position="static"
                color="transparent"
            >
                <Toolbar variant="dense" >
                    <React.Fragment>
                        <Grid>
                            <IconButton
                                edge="start"
                                aria-label="GoBack"
                                color="inherit"
                                size="large"
                                onClick={handleGoBack}
                            >
                                <ArrowBack />
                            </IconButton>
                        </Grid>
                        <Box display={'flex'} justifyContent={'space-between'} alignItems={'center'} width={'100%'}>
                            <div style={{ display: "flex" }} noWrap  >
                                <div>
                                    <Typography >   {`${t('constructionquality.questionnaire')}: ${projectObjectNumber}`}</Typography>
                                </div>
                            </div>
                            <div>
                                <Button variant="contained" color="primary"  disabled={!isAllAnswerGiven}  onClick={handleUpdateStatusChange}  >
                                    {t("general.button.complete")}
                                </Button>

                            </div>
                        </Box>

                    </React.Fragment>
                </Toolbar>
            </AppBar>
            <div style={{ height: '100%', display: "flex", }}>
                {loading ? (
                    <div style={{
                        width: "100%",
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',

                    }}>
                        <CircularProgress size={20} />
                    </div>
                ) : (
                    <ReportDocumentViewer />
                )}
            </div>
        </div>
    );
};

export default PreviewPage;


const useStyles = makeStyles((theme) => ({
    myDoc: {
        background: '#fff !important',
        '& #pdf-download': {
            display: iOSSafari ? "none" : "flex",
        },
        '& #pdf-controls': {
            zIndex: 3
        }
    },
}));
